import { HttpEvent, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { concatMap, from, map, Observable, of, switchMap } from 'rxjs';
import {
  EncryptedEnvelopeV1,
  PAYLOAD_ENCRYPTION,
} from '../constants/payload-encryption.constants';
import { payloadEncryptionConfig, shouldEncryptForUrl } from '../config/payload-encryption.config';
import { PayloadCryptoService } from '../services/payload-crypto.service';

function isEncryptedEnvelopeWrapper(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const inner = (body as Record<string, unknown>)[PAYLOAD_ENCRYPTION.BODY_KEY];
  if (!inner || typeof inner !== 'object') return false;
  const o = inner as Record<string, unknown>;
  return (
    typeof o['iv'] === 'string' &&
    typeof o['data'] === 'string' &&
    typeof o['v'] === 'number'
  );
}

export const payloadEncryptionInterceptor: HttpInterceptorFn = (
  req,
  next,
): Observable<HttpEvent<unknown>> => {
  const cryptoSvc = inject(PayloadCryptoService);

  if (!payloadEncryptionConfig.enabled || !cryptoSvc.isConfigured()) {
    return next(req);
  }

  return from(cloneRequestWithEncryptedBodyIfNeeded(req, cryptoSvc)).pipe(
    switchMap((r) => next(r)),
    concatMap((event) => transformResponseIfEncrypted(event, cryptoSvc)),
  );
};

async function cloneRequestWithEncryptedBodyIfNeeded(
  req: HttpRequest<unknown>,
  svc: PayloadCryptoService,
): Promise<HttpRequest<unknown>> {
  if (!shouldEncryptForUrl(req.url)) return req;
  if (req.method !== 'POST' && req.method !== 'PUT' && req.method !== 'PATCH') {
    return req;
  }
  if (req.body === null || req.body === undefined) return req;
  if (req.body instanceof FormData || req.body instanceof Blob) return req;

  let json: string;
  if (typeof req.body === 'string') {
    json = req.body;
  } else if (req.body instanceof ArrayBuffer) {
    json = new TextDecoder().decode(req.body);
  } else {
    json = JSON.stringify(req.body);
  }

  const envelope = await svc.encryptUtf8(json);
  const wrapped: Record<string, EncryptedEnvelopeV1> = {
    [PAYLOAD_ENCRYPTION.BODY_KEY]: envelope,
  };

  return req.clone({
    body: wrapped,
    setHeaders: {
      [PAYLOAD_ENCRYPTION.HEADER_ENCRYPTED]: '1',
      [PAYLOAD_ENCRYPTION.HEADER_VERSION]: String(PAYLOAD_ENCRYPTION.VERSION),
      'Content-Type': 'application/json',
    },
  });
}

function transformResponseIfEncrypted(
  event: HttpEvent<unknown>,
  svc: PayloadCryptoService,
): Observable<HttpEvent<unknown>> {
  if (!(event instanceof HttpResponse)) {
    return of(event);
  }

  const headerEnc = event.headers.get(PAYLOAD_ENCRYPTION.HEADER_ENCRYPTED);
  const body = event.body;

  if (headerEnc !== '1' && !isEncryptedEnvelopeWrapper(body)) {
    return of(event);
  }

  if (body === null || body === undefined) {
    return of(event);
  }

  return from(decryptResponseBody(body, svc)).pipe(
    map((plain) => {
      let parsed: unknown = plain;
      try {
        parsed = JSON.parse(plain);
      } catch {
        /* keep string */
      }
      return event.clone({ body: parsed });
    }),
  );
}

async function decryptResponseBody(body: unknown, svc: PayloadCryptoService): Promise<string> {
  let envelope: EncryptedEnvelopeV1;

  if (isEncryptedEnvelopeWrapper(body)) {
    envelope = (body as Record<string, EncryptedEnvelopeV1>)[PAYLOAD_ENCRYPTION.BODY_KEY];
  } else if (
    body &&
    typeof body === 'object' &&
    'iv' in body &&
    'data' in body &&
    'v' in body
  ) {
    envelope = body as EncryptedEnvelopeV1;
  } else {
    throw new Error('Unexpected encrypted response shape');
  }

  return svc.decryptToUtf8(envelope);
}
